import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { setDisplayNavBar, setHeaderElements } from 'store/actions/layout';
import InlineButton from 'components/InlineButton';
import { Redirect, useHistory } from 'react-router';
import TechnicalErrorPage from 'views/TechnicalErrorPage';
import config from 'Config';
import SubscriptionsSwitch from 'components/SubscriptionsSwitch';
import useLogout from 'hooks/useLogout';
import { setAppBusy } from 'store/actions/system';
import UserMenu from 'components/UserMenu';
import MenuSwitch from 'components/MenuSwitch';

const PageContainer = ({
  displayNavBar,
  hasBack,
  children,
  render,
  requiresAccount,
  requiresLogin,
  logoRef,
  requiresSubscription,
  hideUserMenu,
}) => {
  const dispatch = useDispatch();
  const user = useSelector((state) => state.user);
  const accountId = useSelector((state) => state.accountId);
  const subscription = useSelector((state) => state.subscription);
  const store = useSelector((state) => state.store);
  const { currentSubscriptionIndex } = useSelector(
    (state) => state.subscriptionsList,
  );
  const subscriptions = useSelector((state) => state.subscriptions);

  const account = useSelector((state) => state.account);
  const subscriptionLoadFailed = useSelector(
    (state) => state.subscriptionLoadFailed,
  );

  const history = useHistory();
  const performLogout = useLogout();

  const BackButton = () => (
    <InlineButton chevron="left" type="button" onClick={history.goBack}>
      Back
    </InlineButton>
  );

  useEffect(() => {
    dispatch(setDisplayNavBar(displayNavBar));
    dispatch(
      setHeaderElements(
        {
          back: (hasBack && <BackButton />) || null,
          subscriptions: (!hasBack && subscriptions && subscriptions.length > 1 && (
              <SubscriptionsSwitch
                selectedIndex={currentSubscriptionIndex}
                subscriptions={subscriptions}
              />
            ))
            || null,
          menu: (!hasBack && user && <MenuSwitch labelComponent={'Menu'} />)
            || null,
          user:
            (user && !hideUserMenu && <UserMenu />)
            || null,
          logoRef,
        } || null,
      ),
    );
  }, [
    dispatch,
    displayNavBar,
    hasBack,
    user,
    logoRef,
    subscriptions,
    currentSubscriptionIndex,
    hideUserMenu,
  ]);

  const waitingForData = requiresSubscription && (!account || !subscription);

  React.useEffect(() => {
    dispatch(setAppBusy(waitingForData));
  }, [waitingForData, dispatch]);

  if (requiresAccount && !accountId) {
    return <Redirect to="/find_account" />;
  }

  if (requiresLogin && !user) {
    return <Redirect to="/" />;
  }

  if (requiresSubscription && subscriptionLoadFailed) {
    dispatch(setDisplayNavBar(false));
    dispatch(
      setHeaderElements(
        {
          left: (
            <InlineButton type="button" chevron="left" onClick={performLogout}>
              Back
            </InlineButton>
          ),
          right: null,
          logoRef,
        } || null,
      ),
    );
    dispatch(setAppBusy(false));
    return (
      <TechnicalErrorPage
        displayButton
        customerServicePhoneNumber={config.customerServicePhoneNumber}
      />
    );
  }

  return !waitingForData && (children || (render && render(
    subscription,
    account,
    store,
  )));
};

PageContainer.propTypes = {
  displayNavBar: PropTypes.bool,
  children: PropTypes.oneOfType([
    PropTypes.node,
    PropTypes.arrayOf(PropTypes.node),
  ]),
  render: PropTypes.func,
  hasBack: PropTypes.bool,
  requiresAccount: PropTypes.bool,
  requiresLogin: PropTypes.bool,
  requiresSubscription: PropTypes.bool,
  logoRef: PropTypes.string,
  hideUserMenu: PropTypes.bool,
};

PageContainer.defaultProps = {
  displayNavBar: false,
  hasBack: false,
  children: null,
  requiresAccount: false,
  requiresLogin: false,
  requiresSubscription: false,
  logoRef: null,
  hideUserMenu: false,
  render: null,
};

PageContainer.displayName = 'PageContainer';

export default PageContainer;
